import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../services/firebase';
import { useAuth } from './AuthContext';
import { handleAuthError } from '../utils/authErrorHandler';

const SubscriptionContext = createContext(null);

export function useSubscription() {
  const ctx = useContext(SubscriptionContext);
  if (!ctx) throw new Error('useSubscription must be inside SubscriptionProvider');
  return ctx;
}

const PLAN_DISPLAY = {
  free: {
    name: 'Free',
    generationsLabel: '10 total',
    circlesLabel: '1 circle',
    price: null,
  },
  family: {
    name: 'Family',
    generationsLabel: '200 / month',
    circlesLabel: 'Unlimited circles',
    price: '$9.99/mo',
  },
  family_plus: {
    name: 'Family Plus',
    generationsLabel: '400 / month',
    circlesLabel: 'Unlimited circles',
    price: '$19.99/mo',
  },
  family_pro: {
    name: 'Family Pro',
    generationsLabel: '600 / month',
    circlesLabel: 'Unlimited circles',
    price: '$34.99/mo',
  },
};

export function SubscriptionProvider({ children }) {
  const { currentUser } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentUser?.uid) {
      setSubscription(null);
      setLoading(false);
      return;
    }

    const subRef = doc(db, 'users', currentUser.uid, 'subscription', 'current');
    const unsubscribe = onSnapshot(
      subRef,
      (snap) => {
        if (snap.exists()) {
          setSubscription(snap.data());
        } else {
          setSubscription(null);
        }
        setLoading(false);
      },
      (error) => {
        console.error('Subscription listener error:', error);
        handleAuthError(error);
        setLoading(false);
      }
    );

    return unsubscribe;
  }, [currentUser?.uid]);

  const planId = subscription?.planId || 'free';
  const display = PLAN_DISPLAY[planId] || PLAN_DISPLAY.free;
  const generationsUsed = subscription?.generationsUsed || 0;
  const generationsLimit = subscription?.generationsLimit || 10;
  const isLifetimeLimit = subscription?.isLifetimeLimit ?? true;
  const canGenerate = generationsUsed < generationsLimit;
  const remaining = Math.max(0, generationsLimit - generationsUsed);

  const startCheckout = useCallback(
    async (targetPlanId) => {
      if (!currentUser?.uid) return;
      const planMapping = {
        'family': 'app-family',
        'family_plus': 'app-family-plus',
        'family_pro': 'app-family-pro'
      };
      const stripePlanId = planMapping[targetPlanId] || 'app-family';
      const createCheckout = httpsCallable(functions, 'createSubscriptionCheckout');
      const result = await createCheckout({ planId: stripePlanId, returnUrl: '/art' });
      const data = result.data;
      if (data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
      }
      return data;
    },
    [currentUser?.uid]
  );

  const value = {
    subscription,
    loading,
    planId,
    display,
    generationsUsed,
    generationsLimit,
    isLifetimeLimit,
    canGenerate,
    remaining,
    isPaid: planId !== 'free',
    isFamily: planId === 'family',
    isFamilyPlus: planId === 'family_plus',
    isFamilyPro: planId === 'family_pro',
    allowFluxPro: subscription?.allowFluxPro || false,
    printDiscount: subscription?.printDiscount || 0,
    circleLimit: subscription?.circleLimit ?? 1,
    startCheckout,
    PLAN_DISPLAY,
  };

  return <SubscriptionContext.Provider value={value}>{children}</SubscriptionContext.Provider>;
}
